import React from 'react';
import { useTranslation } from 'react-i18next';
import { PricingCard } from '@/components/card/pricing-card';
import { Link } from '@tanstack/react-router';
import { motion } from 'framer-motion';
import { container, fadeUp } from '@/constants/motion';
import { ArrowRight, ShieldCheck, RefreshCw, Gift } from 'lucide-react';
import { cn } from '@/lib/utils';
import { getPlans } from '@/billing/plan';
export const Pricing = () => {
    const { t } = useTranslation('marketing');
    const [billing, setBilling] = React.useState('annual');
    const plans = getPlans(billing);
    return (<motion.section className="relative overflow-hidden border-t bg-linear-to-b from-muted/40 to-background" initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-80px' }} variants={container}>
            {/* Decorative top line */}
            <div className="pointer-events-none absolute top-0 left-1/2 -translate-x-1/2 h-px w-1/2 bg-linear-to-r from-transparent via-primary/30 to-transparent"/>

            <div className="relative mx-auto max-w-6xl px-4 py-20">
                {/* Header */}
                <motion.p variants={fadeUp} className="text-center text-xs font-semibold uppercase tracking-widest text-primary mb-3">
                    Pricing
                </motion.p>
                <motion.h2 className="text-center text-3xl font-bold md:text-4xl" variants={fadeUp}>
                    {t('pricing.hero.title')}
                </motion.h2>
                <motion.p className="mt-3 text-center text-sm text-muted-foreground md:text-base max-w-xl mx-auto leading-relaxed" variants={fadeUp}>
                    {t('pricing.hero.subtitle')}
                </motion.p>

                {/* Billing toggle */}
                <motion.div className="mt-8 flex justify-center" variants={fadeUp}>
                    <div className="inline-flex items-center gap-0.5 rounded-full bg-muted p-1">
                        <button className={cn('rounded-full px-4 py-1.5 text-sm font-medium transition-all duration-200', billing === 'monthly'
            ? 'bg-background text-foreground shadow-sm'
            : 'text-muted-foreground hover:text-foreground')} onClick={() => setBilling('monthly')}>
                            Monthly
                        </button>
                        <button className={cn('inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-sm font-medium transition-all duration-200', billing === 'annual'
            ? 'bg-background text-foreground shadow-sm'
            : 'text-muted-foreground hover:text-foreground')} onClick={() => setBilling('annual')}>
                            Annual
                            <span className="rounded-full bg-primary px-1.5 py-0.5 text-[10px] font-semibold text-primary-foreground">
                                2 months free
                            </span>
                        </button>
                    </div>
                </motion.div>

                {/* Pricing cards */}
                <div className="pointer-events-none absolute left-1/2 -translate-x-1/2 h-80 w-80 rounded-full bg-primary/8 blur-3xl"/>
                <motion.div className="relative mt-10 grid gap-6 md:grid-cols-3 md:items-stretch" variants={container}>
                    {plans.map((plan) => (<motion.div key={plan.code} variants={fadeUp} className="h-full">
                            <PricingCard name={plan.name} price={plan.price} priceHint={plan.priceHint} annualNote={plan.annualNote} description={plan.description} featuresLabel={plan.featuresLabel} footerNote={plan.footerNote} items={plan.items} cta={plan.cta} highlight={plan.highlighted}/>
                        </motion.div>))}
                </motion.div>

                {/* Trust strip */}
                <motion.div className="mt-8 flex flex-wrap justify-center gap-x-8 gap-y-2" variants={fadeUp}>
                    <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
                        <ShieldCheck className="h-3.5 w-3.5 text-primary"/>
                        No credit card required
                    </span>
                    <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
                        <RefreshCw className="h-3.5 w-3.5 text-primary"/>
                        Cancel anytime
                    </span>
                    <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
                        <Gift className="h-3.5 w-3.5 text-primary"/>2 free campaigns, always
                    </span>
                </motion.div>

                {/* Footer CTA */}
                <motion.div className="mt-8 text-center" variants={fadeUp}>
                    <Link to="/pricing" className="inline-flex items-center gap-2 text-sm font-semibold text-primary hover:underline">
                        {t('pricing.viewFullDetails')}
                        <ArrowRight className="h-4 w-4"/>
                    </Link>
                </motion.div>
            </div>
        </motion.section>);
};
Pricing.displayName = 'Pricing';
